'use client';

import React, { useState } from 'react';
import { cn } from '@/lib/utils/cn';
import { Logo } from '@/components/ui/logo';
import { SocialLinks } from '@/components/ui/social-links';
import { ArrowUp } from 'lucide-react';
import { About } from '@/types';
import type { LandingPage } from '@/types/landingPage';
import { useFooter } from '@/contexts/FooterContext';
import { useTenant } from '@/contexts/TenantContext';
import { NewsletterAPI } from '@/lib/services/newsletter.service';
import { toast } from '@/hooks/use-toast';
import { getCurrentYear } from '@/lib/utils/date-format';
import { backgroundPlaybackUrl } from '@/lib/utils/media.utils';
import { ArchivalColophon } from '@/components/layout/footer';
import { LoopVideo } from '@/components/ui/catalog/bento/loop-video';



export function MegaFooter({ contactData, aboutData: _aboutData }: { contactData: LandingPage, aboutData: About }) {
  const { footerMode, setIsFooterOpen } = useFooter();
  const { isOrkhan, shortName, tagline } = useTenant();

  // Newsletter state
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async () => {
    // Honeypot check - bot detected
    if (honeypot) {
      setEmail('');
      toast({ title: 'Successfully subscribed!' });
      return;
    }

    if (!email) return;

    setIsSubmitting(true);

    const result = await NewsletterAPI.subscribe(email);

    if (result.success) {
      setEmail('');
      toast({ title: 'Successfully subscribed to newsletter!' });
    } else {
      toast({ title: result.error || 'Subscription failed.', variant: 'destructive' });
    }

    setIsSubmitting(false);
  };

  // Scroll to top or close footer in toggle mode
  const handleBackToTop = () => {
    if (footerMode === 'toggle') {
      setIsFooterOpen(false);
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const showreelSrc =
    backgroundPlaybackUrl(contactData?.showreel) ||
    (isOrkhan ? '/media/showreel/showreel.mp4' : '');

  return (
    <>
      <section className="min-h-screen w-full bg-black text-white flex flex-col justify-between py-16 footer-container relative overflow-hidden">
        {/* Loads and plays only while the footer is on screen. With autoplay, every page of the
            site downloaded the showreel as it opened: half of what an average page sent. */}
        {showreelSrc && (
          <LoopVideo
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-30"
            src={showreelSrc}
          />
        )}
        <div className="footer-sep">
          <div className="footer-sep-mask"></div>
        </div>
        {/* Top section with logo and copyright */}
        <div className="relative z-10 w-full px-4 flex flex-row justify-between items-start">

          {/* Logo */}
          <div className="mb-12 md:mb-0 mr-5">
            {isOrkhan ? (
              <div className="w-24 h-24 relative">
                <Logo />
              </div>
            ) : (
              <div className="font-superior text-3xl md:text-4xl uppercase tracking-tight text-white">
                {shortName}
              </div>
            )}
          </div>

          {/* Right side - back to top button + copyright */}
          <div className="flex flex-col items-end gap-4">
            {/* Back to top button */}
            <button
              onClick={handleBackToTop}
              className={cn(
                'p-3 bg-zinc-800 hover:bg-zinc-700 rounded-full shadow-lg transition-all duration-300'
              )}
              aria-label="Back to top"
            >
              <ArrowUp className="w-4 h-4 text-white" />
            </button>

            {/* Copyright text */}
            <div className="text-[10px] uppercase tracking-wider max-w-md text-right">
              <p className="leading-tight">{tagline}</p>
              <p className="mt-4 text-right">{getCurrentYear()}®</p>
            </div>
          </div>
        </div>


        {/* Bottom section: Follow Artist (3/4) + Newsletter (1/4) */}
        <div className="relative z-10 w-full px-4 mt-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 items-start">
            {/* Follow Artist - 3 columns */}
            <div className="md:col-span-3">
              <h3 className="font-mono text-[10px] uppercase tracking-wider opacity-50 mb-6">
                [ FOLLOW {shortName} ]
              </h3>
              <SocialLinks contactData={contactData} />
              {/*
                The only link from the art site to the design portfolio. Without
                it orkhan.design has no inbound links at all, which is why it does
                not rank for anything. Descriptive anchor text on purpose: "Design"
                alone tells a search engine nothing about what is on the far side.
              */}
              {isOrkhan && (
                <p className="font-mono text-[10px] uppercase tracking-wider opacity-50 mt-8">
                  <a
                    href="https://orkhan.design"
                    rel="me"
                    className="hover:opacity-100 transition-opacity underline underline-offset-4"
                  >
                    Creative technology and product design work
                  </a>
                </p>
              )}
            </div>

            {/* Newsletter - 1 column */}
            {contactData.newsletterTitle && (
              <div className="md:col-span-1">
                <h3 className="font-mono text-[10px] uppercase tracking-wider opacity-50 mb-6">
                  [ {contactData.newsletterTitle} ]
                </h3>
                <div className="flex flex-col gap-2">
                  {/* Honeypot field - hidden from users, visible to bots */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    className="absolute -left-[9999px]"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-[10px] focus:outline-none focus:border-white transition-colors placeholder:text-zinc-600 rounded"
                  />
                  <button
                    type="button"
                    disabled={isSubmitting || !email}
                    onClick={handleSubscribe}
                    className="bg-white text-black px-6 py-2 text-[10px] uppercase font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50 rounded flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        SUBSCRIBING
                      </>
                    ) : (
                      'SUBSCRIBE'
                    )}
                  </button>
                  <p className="font-mono text-[8px] opacity-40 mt-2 uppercase leading-relaxed">
                    No spam, just important updates about drops and exhibitions.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              © {getCurrentYear()} {shortName}
            </span>
            <ArchivalColophon variant="dark" />
          </div>
        </div>
      </section>

    </>
  );
}

